import { sanitizeReportPostalCodeInput } from "./arizona-postal-code.js";

export const ACCOUNT_REPORT_DRAFT_KEY = "community-report-pending-account-flow";
export const ACCOUNT_REPORT_SUBMISSION_IDS_KEY = "community-report-pending-submission-ids";
export const ACCOUNT_REPORT_SUBMISSION_IDS_VERSION = 1;
export const ACCOUNT_REPORT_DRAFT_VERSION = 2;
export const ACCOUNT_REPORT_DRAFT_TTL_MS = 30 * 60 * 1000;
export const ANOTHER_PERSON_RESUME_PATH = "/#/people/other/feeling";
export const HANDOFF_NONCE_PARAMETER = "handoff";

const CATEGORIES = new Set(["people", "animals", "environment"]);
const SUBJECTS = new Set(["self", "other"]);

function text(value, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function stringList(value) {
  return Array.isArray(value) ? value.filter((item) => typeof item === "string") : [];
}

function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function isValidHandoffNonce(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "")
  );
}

export function createHandoffNonce(randomUUID = () => globalThis.crypto.randomUUID()) {
  const nonce = String(randomUUID()).toLowerCase();
  if (!isValidHandoffNonce(nonce)) throw new Error("Could not create a valid report handoff nonce");
  return nonce;
}

function isValidAccountBinding(value) {
  return value === "" || /^[0-9a-f]{64}$/.test(String(value || ""));
}

export async function handoffAccountBinding(email, nonce, subtle = globalThis.crypto.subtle) {
  if (!isValidHandoffNonce(nonce)) return "";
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!normalizedEmail) return "";
  const input = new globalThis.TextEncoder().encode(`${nonce}\u0000${normalizedEmail}`);
  const digest = await subtle.digest("SHA-256", input);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function handoffNonceFromResumePath(value) {
  const cleaned = String(value || "").trim();
  const [path, query = ""] = cleaned.split("?");
  if (path !== ANOTHER_PERSON_RESUME_PATH) return "";
  const params = new URLSearchParams(query);
  const nonce = params.get(HANDOFF_NONCE_PARAMETER) || "";
  return isValidHandoffNonce(nonce) && [...params.keys()].every((key) => key === HANDOFF_NONCE_PARAMETER)
    ? nonce.toLowerCase()
    : "";
}

export function anotherPersonResumePath(nonce) {
  if (!isValidHandoffNonce(nonce)) return "";
  return `${ANOTHER_PERSON_RESUME_PATH}?${new URLSearchParams({ [HANDOFF_NONCE_PARAMETER]: nonce })}`;
}

function exposureDraft(value = {}) {
  return {
    massGathering: text(value.massGathering),
    massGatheringEvent: text(value.massGatheringEvent),
    liveAnimalContact: text(value.liveAnimalContact),
    liveAnimalBitten: text(value.liveAnimalBitten),
    liveAnimalAppearedSick: text(value.liveAnimalAppearedSick),
    deadAnimalContact: text(value.deadAnimalContact),
    sickIndividualContact: text(value.sickIndividualContact),
    tickOrInsectBite: text(value.tickOrInsectBite),
    recentTravel: text(value.recentTravel),
    recentTravelLocation: text(value.recentTravelLocation),
  };
}

function peopleReportDraft(value = {}) {
  return {
    feeling: text(value.feeling),
    symptoms: stringList(value.symptoms),
    otherSymptom: text(value.otherSymptom),
    sickCount: text(value.sickCount, "1"),
    zip: sanitizeReportPostalCodeInput(text(value.zip)),
    onset: text(value.onset),
    diagnosed: text(value.diagnosed),
    diagnosis: text(value.diagnosis),
    calledInSick: text(value.calledInSick),
    exposures: exposureDraft(value.exposures),
  };
}

export function createAccountReportDraft(state, createdAt = Date.now(), handoffNonce = createHandoffNonce()) {
  if (!isValidHandoffNonce(handoffNonce)) throw new Error("A valid report handoff nonce is required");
  return {
    version: ACCOUNT_REPORT_DRAFT_VERSION,
    created_at: createdAt,
    resume_path: ANOTHER_PERSON_RESUME_PATH,
    handoff_nonce: handoffNonce.toLowerCase(),
    account_binding: "",
    categories: stringList(state.categories).filter((category) => CATEGORIES.has(category)),
    report_subjects: stringList(state.reportSubjects).filter((subject) => SUBJECTS.has(subject)),
    people_reports: {
      self: peopleReportDraft(state.peopleReports?.self),
      other: peopleReportDraft(state.peopleReports?.other),
    },
  };
}

export function saveAccountReportDraft(
  storage,
  state,
  createdAt = Date.now(),
  handoffNonce = createHandoffNonce()
) {
  const draft = createAccountReportDraft(state, createdAt, handoffNonce);
  storage.setItem(ACCOUNT_REPORT_DRAFT_KEY, JSON.stringify(draft));
  return draft;
}

function removeStoredAccountReportDraft(storage) {
  try {
    storage.removeItem(ACCOUNT_REPORT_DRAFT_KEY);
  } catch {
    // Browser storage can be unavailable. Draft cleanup must not block reporting.
  }
}

export function loadAccountReportDraft(storage, now = Date.now()) {
  try {
    const draft = JSON.parse(storage.getItem(ACCOUNT_REPORT_DRAFT_KEY) || "null");
    const age = now - draft?.created_at;
    if (
      !draft
      || draft.version !== ACCOUNT_REPORT_DRAFT_VERSION
      || !Number.isFinite(draft.created_at)
      || !Number.isFinite(age)
      || age < 0
      || age > ACCOUNT_REPORT_DRAFT_TTL_MS
      || draft.resume_path !== ANOTHER_PERSON_RESUME_PATH
      || !isValidHandoffNonce(draft.handoff_nonce)
      || !isValidAccountBinding(draft.account_binding)
      || !Array.isArray(draft.categories)
      || !Array.isArray(draft.report_subjects)
      || !isRecord(draft.people_reports)
      || !isRecord(draft.people_reports.self)
      || !isRecord(draft.people_reports.other)
    ) {
      removeStoredAccountReportDraft(storage);
      return null;
    }
    const normalized = createAccountReportDraft(
      {
        categories: draft.categories,
        reportSubjects: draft.report_subjects,
        peopleReports: draft.people_reports,
      },
      draft.created_at,
      draft.handoff_nonce
    );
    normalized.account_binding = draft.account_binding;
    if (!normalized.categories.includes("people") || !normalized.report_subjects.includes("other")) {
      removeStoredAccountReportDraft(storage);
      return null;
    }
    return normalized;
  } catch {
    removeStoredAccountReportDraft(storage);
    return null;
  }
}

export function prepareAccountReportDraftForAppStart(storage, now = Date.now()) {
  return loadAccountReportDraft(storage, now);
}

export function clearAccountReportDraft(storage) {
  removeStoredAccountReportDraft(storage);
}

export function clearAccountReportSubmissionIds(storage) {
  try {
    storage.removeItem(ACCOUNT_REPORT_SUBMISSION_IDS_KEY);
  } catch {
    // Tab storage can be unavailable. Cleanup must not block reporting.
  }
}

export function assignAccountReportSubmissionIds(storage, batchId, slots, payloads, idFactory) {
  const normalizedBatchId = String(batchId || "").toLowerCase();
  if (!isValidHandoffNonce(normalizedBatchId)) {
    throw new Error("A valid report submission batch ID is required");
  }
  const normalizedSlots = stringList(slots);
  if (!Array.isArray(payloads) || normalizedSlots.length !== payloads.length) return payloads;

  let storedIds = {};
  try {
    const stored = JSON.parse(storage.getItem(ACCOUNT_REPORT_SUBMISSION_IDS_KEY) || "null");
    if (
      isRecord(stored)
      && stored.version === ACCOUNT_REPORT_SUBMISSION_IDS_VERSION
      && stored.batch_id === normalizedBatchId
      && isRecord(stored.report_ids)
    ) {
      storedIds = stored.report_ids;
    }
  } catch {
    storedIds = {};
  }

  const nextIds = { ...storedIds };
  const assignedPayloads = payloads.map((payload, index) => {
    const slot = normalizedSlots[index];
    const storedId = nextIds[slot];
    const id = isValidHandoffNonce(storedId) ? storedId : String(idFactory()).toLowerCase();
    if (!isValidHandoffNonce(id)) throw new Error("Could not create a valid report submission ID");
    nextIds[slot] = id;
    return { ...payload, id };
  });
  storage.setItem(ACCOUNT_REPORT_SUBMISSION_IDS_KEY, JSON.stringify({
    version: ACCOUNT_REPORT_SUBMISSION_IDS_VERSION,
    batch_id: normalizedBatchId,
    report_ids: nextIds,
  }));
  return assignedPayloads;
}

export function accountRegistrationUrl(handoffNonce) {
  const returnPath = anotherPersonResumePath(handoffNonce);
  if (!returnPath) return "/accounts#/register";
  const params = new URLSearchParams({ return: returnPath });
  return `/accounts#/register?${params}`;
}

export function prepareAccountReportRegistration(
  storage,
  state,
  createdAt = Date.now(),
  handoffNonce = createHandoffNonce()
) {
  try {
    const draft = saveAccountReportDraft(storage, state, createdAt, handoffNonce);
    return {
      ok: true,
      destination: accountRegistrationUrl(draft.handoff_nonce),
      error: "",
    };
  } catch (error) {
    return {
      ok: false,
      destination: "",
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export function resumeAccountReportRegistration(storage, resumePath, now = Date.now()) {
  const draft = loadAccountReportDraft(storage, now);
  const routeNonce = handoffNonceFromResumePath(resumePath);
  if (!draft || !routeNonce || draft.handoff_nonce !== routeNonce) return null;
  return {
    ok: true,
    destination: accountRegistrationUrl(draft.handoff_nonce),
    error: "",
  };
}

export async function accountReportDraftMatchesAuthenticatedAccount(draft, resumePath, email) {
  const routeNonce = handoffNonceFromResumePath(resumePath);
  const accountBinding = await handoffAccountBinding(email, routeNonce);
  return Boolean(
    draft
    && routeNonce
    && routeNonce === draft.handoff_nonce
    && draft.account_binding
    && accountBinding === draft.account_binding
  );
}

export async function loadAccountReportDraftForAuthenticatedResume(storage, resumePath, email, now = Date.now()) {
  const draft = loadAccountReportDraft(storage, now);
  if (!draft) return null;
  if (!await accountReportDraftMatchesAuthenticatedAccount(draft, resumePath, email)) {
    clearAccountReportDraft(storage);
    return null;
  }
  return draft;
}

export function isAuthenticatedAnotherPersonSubmission({ accountSignedIn, reportSubjects }) {
  return accountSignedIn === true && stringList(reportSubjects).includes("other");
}

export function authenticatedSubmissionIsComplete({ accountSignedIn, reportSubjects, payloadCount, result }) {
  const completedCount = Number(result.accepted || 0) + Number(result.deduped || 0);
  return (
    isAuthenticatedAnotherPersonSubmission({ accountSignedIn, reportSubjects }) &&
    Number(result.rejected || 0) === 0 &&
    completedCount === payloadCount
  );
}
